import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { AdminShell } from '@/components/layout/AdminShell';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api-client';
import { PAYMENT_STATUS, type PaymentStatus } from '@parecer/shared';
import { formatBRL, formatRelativeDate } from '@/lib/utils';

interface PaymentItem {
  paymentId: string;
  taskId: string;
  pagarmeOrderId: string;
  pagarmeChargeId: string;
  amount: number;
  status: PaymentStatus;
  method: 'pix' | 'credit_card' | 'boleto';
  splitLawyerAmount: number;
  splitPlatformAmount: number;
  escrowReleased: boolean;
  createdAt: string;
}

export function AdminPagamentos() {
  const [filter, setFilter] = useState<string>('');
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'payments', filter],
    queryFn: () => api.get<{ items: PaymentItem[] }>(`/admin/payments?limit=200${filter ? `&status=${filter}` : ''}`),
  });

  return (
    <AdminShell>
      <div className="max-w-7xl">
        <h1 className="font-serif text-display-lg">Pagamentos</h1>
        <p className="text-muted-foreground mt-1">{data?.items.length ?? 0} transações.</p>

        <div className="mt-6 flex flex-wrap gap-2">
          <Button size="sm" variant={filter === '' ? 'accent' : 'outline'} onClick={() => setFilter('')}>
            Todos
          </Button>
          {Object.values(PAYMENT_STATUS).map((s) => (
            <Button key={s} size="sm" variant={filter === s ? 'accent' : 'outline'} onClick={() => setFilter(s)}>
              {s}
            </Button>
          ))}
        </div>

        <Card className="mt-6">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b border-border/60 text-left text-muted-foreground">
                <tr>
                  <th className="p-3 font-medium">Pagar.me Charge</th>
                  <th className="p-3 font-medium">Método</th>
                  <th className="p-3 font-medium">Status</th>
                  <th className="p-3 font-medium text-right">Total</th>
                  <th className="p-3 font-medium text-right">Plataforma</th>
                  <th className="p-3 font-medium text-right">Advogado</th>
                  <th className="p-3 font-medium text-right">Criado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {isLoading && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Carregando…</td></tr>}
                {data && data.items.length === 0 && (
                  <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Nenhum pagamento.</td></tr>
                )}
                {data?.items.map((p) => (
                  <tr key={p.paymentId} className="hover:bg-muted/30 transition-colors">
                    <td className="p-3 font-mono text-xs">{p.pagarmeChargeId ?? p.pagarmeOrderId ?? '—'}</td>
                    <td className="p-3 text-xs uppercase">{methodLabel(p.method)}</td>
                    <td className="p-3"><Badge variant={paymentVariant(p.status)}>{p.status}</Badge></td>
                    <td className="p-3 text-right font-medium">{formatBRL(p.amount)}</td>
                    <td className="p-3 text-right text-muted-foreground">{formatBRL(p.splitPlatformAmount)}</td>
                    <td className="p-3 text-right text-muted-foreground">{formatBRL(p.splitLawyerAmount)}</td>
                    <td className="p-3 text-right text-xs text-muted-foreground">{formatRelativeDate(p.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </AdminShell>
  );
}

function paymentVariant(s: string): 'default' | 'success' | 'warning' | 'danger' | 'muted' {
  if (s === 'released' || s === 'confirmed') return 'success';
  if (s === 'pending' || s === 'escrowed') return 'warning';
  if (s === 'failed' || s === 'refunded') return 'danger';
  return 'muted';
}

function methodLabel(m: string): string {
  return { pix: 'PIX', credit_card: 'Cartão', boleto: 'Boleto' }[m] ?? m;
}
